import {Button, Cascader, CascaderProps, Divider, Dropdown, Flex, MenuProps, Select, Space, Tooltip} from "antd";
import {useLazySearchAssetQuery} from "../../store/services/api/asset-api";
import {
  useGetBuildingCascaderOptionsQuery
} from "../../store/services/api/building-api";
import {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {CascaderOption} from "../../store/data/models/cascader-option";
import useDebounce from "../../hooks/useDebounce";
import {useGetAssetTypeCascaderOptionsQuery} from "../../store/services/api/asset-type-api";
import {CloseOutlined, FilterOutlined, GlobalOutlined, WarningOutlined} from "@ant-design/icons";
import WorkOrderAssetOpenWorkOrdersModal from "../modals/WorkOrderAssetOpenWorkOrdersModal";

export type LabelValue = {
  value: string;
  label: ReactNode;
}

interface AssetFormControlProps {
  onChange?: (value: LabelValue[] | LabelValue) => void;
  value?: LabelValue[] | LabelValue | undefined;
  disabled?: boolean;
  multiple?: boolean;
  enableEdit?: boolean;
  menuPlacement?: CascaderProps['placement'];
}

export default function AssetFormControl(props: AssetFormControlProps) {
  const {data: buildingOptions, isFetching: buildingOptionsFetching} = useGetBuildingCascaderOptionsQuery(null, {
    skip: props.disabled
  });
  const {data: assetTypeOptions, isFetching: assetTypeOptionsFetching} = useGetAssetTypeCascaderOptionsQuery(null, {
    skip: props.disabled
  });
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | undefined>(undefined);
  const [selectedFloorId, setSelectedFloorId] = useState<string | undefined>(undefined);
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);

  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<string | undefined>(undefined);
  const [selectedSubAssetTypeId, setSelectedSubAssetTypeId] = useState<string | undefined>(undefined);

  const [buildingCascaderValue, setBuildingCascaderValue] = useState<string[] | undefined>();
  const [assetTypeCascaderValue, setAssetTypeCascaderValue] = useState<string[] | undefined>();

  const [initialLoad, setInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [searchAssets, {data: assetOptions, isFetching: assetsFetching}] = useLazySearchAssetQuery();

  useEffect(() => {
    if (initialLoad && !debouncedSearchQuery) {
      return;
    }
    handleOnSearchAssets(debouncedSearchQuery);
  }, [
    debouncedSearchQuery, selectedBuildingId,
    selectedFloorId, selectedLocationId,
    selectedAssetTypeId, selectedSubAssetTypeId,
    initialLoad
  ]);

  useEffect(() => {
  if (props.value && initialLoad) {
    setInitialLoad(false);
    handleOnSearchAssets();
  }
}, [props.value]);

  const handleOnSearchAssets = (value?: string | undefined) => {
    searchAssets({
      searchTerm: value === '' ? undefined : value,
      buildingId: selectedBuildingId,
      floorId: selectedFloorId,
      locationId: selectedLocationId,
      assetTypeId: selectedAssetTypeId,
      subAssetTypeId: selectedSubAssetTypeId,
    })
  }

  const onBuildingFilterChange: CascaderProps<CascaderOption>['onChange'] = (selectedItem: string[]) => {
    const isBuildingSelected = selectedItem.length === 1;
    const isFloorSelected = selectedItem.length === 2;
    const isLocationSelected = selectedItem.length === 3;
    setBuildingCascaderValue(selectedItem);

    if (isBuildingSelected) {
      setSelectedBuildingId(selectedItem[0]);
      setSelectedFloorId(undefined);
      setSelectedLocationId(undefined);
      return;
    }
    if (isFloorSelected) {
      setSelectedFloorId(selectedItem[1]);
      setSelectedLocationId(undefined);
      return;
    }
    if (isLocationSelected) {
      setSelectedLocationId(selectedItem[2]);
      return;
    }
  };

  const onAssetTypeFilterChange: CascaderProps<CascaderOption>['onChange'] = (selectedItem: string[]) => {
    const isAssetTypeSelected = selectedItem.length === 1;
    const isSubAssetType = selectedItem.length === 2;
    setAssetTypeCascaderValue(selectedItem)

    if (isAssetTypeSelected) {
      setSelectedAssetTypeId(selectedItem[0]);
      setSelectedSubAssetTypeId(undefined);
      return;
    }
    if (isSubAssetType) {
      setSelectedSubAssetTypeId(selectedItem[1]);
      return;
    }
  };

  const onFilterClear = (type: 'building' | 'assetType') => {
    if (type === 'building') {
      setBuildingCascaderValue(undefined);

      setSelectedBuildingId(undefined);
      setSelectedFloorId(undefined);
      setSelectedLocationId(undefined);
      return;
    }

    if (type === 'assetType') {

      setAssetTypeCascaderValue(undefined);

      setSelectedAssetTypeId(undefined);
      setSelectedSubAssetTypeId(undefined);
      return;
    }
  }

  const handleOnChange = (value: LabelValue[] | LabelValue) => {
    props.onChange?.(value);
  }

  const handleOnSelectFocus = () => {
    if (initialLoad) {
      setInitialLoad(false);
      handleOnSearchAssets();
    }
  }
  
  const allSelectedInList = useMemo(() => {
    if (!assetOptions?.length || !props?.value) return false;

    return Array.isArray(props.value)
      ? assetOptions.every(option => (props.value as LabelValue[]).some(value => value?.value === option.value))
      : assetOptions.some(option => (props.value as LabelValue)?.value === option.value);
  }, [assetOptions, props.value]);

  const handleOnSelectAll = useCallback(() => {
    if (!assetOptions?.length) return false;

    const newOptions = assetOptions.filter(option => {
      if (Array.isArray(props?.value)) {
        return !(props.value as LabelValue[]).some(value => value.value === option.value);
      }

      return (props?.value as LabelValue)?.value !== option.value;
    }).map(option => ({
      value: option.value,
      label: option.label
    }));

    if (Array.isArray(props.value) && props.value.length > 0) {
      handleOnChange([
        ...props.value,
        ...newOptions
      ]);
      return;
    }

    if ((props.value as LabelValue)?.value) {
      handleOnChange([
        (props.value as LabelValue),
        ...newOptions
      ]);
      return;
    }

    handleOnChange([
      ...newOptions
    ]);

  }, [assetOptions, props.value, allSelectedInList, handleOnChange]);

  const selectedCount = useMemo(() => {
    return Array.isArray(props.value)
      ? props.value.length
      : props.value ? 1 : 0;
  }, [props.value]);

  const locationFilterActive = buildingCascaderValue?.length > 0;
  const assetTypeFilterActive = assetTypeCascaderValue?.length > 0;
  const filterActive = locationFilterActive || assetTypeFilterActive;

  const filterMenu: MenuProps = {
    items: [
      {
        key: 'location',
        label: (
          <div onClick={(e) => e.stopPropagation()}>
            <Cascader
              options={buildingOptions}
              disabled={props.disabled}
              onChange={onBuildingFilterChange}
              value={buildingCascaderValue}
              loading={buildingOptionsFetching}
              onClear={() => onFilterClear('building')}
              changeOnSelect
              dropdownRender={(menu) => menu}
              placement={props.menuPlacement}
            >
              <Button
                variant='link'
                icon={<GlobalOutlined />}
                iconPosition='start'
                color={locationFilterActive ? 'blue' : 'default'}
              >
                Location
              </Button>
            </Cascader>
          </div>
        ),
        extra: (
          <Tooltip title="Clear filter">
            <Button
              icon={<CloseOutlined />}
              disabled={props.disabled}
              onClick={(e) => {
                e.stopPropagation();
                onFilterClear("building")
              }}
            />
          </Tooltip>
        )
      },
      {
        key: 'assetType',
        label: (
          <div onClick={(e) => e.stopPropagation()}>
            <Cascader
              options={assetTypeOptions}
              disabled={props.disabled}
              onChange={onAssetTypeFilterChange}
              value={assetTypeCascaderValue}
              loading={assetTypeOptionsFetching}
              onClear={() => onFilterClear('assetType')}
              changeOnSelect
              dropdownRender={(menu) => menu}
              placement={props.menuPlacement}
            >
              <Button
                variant='link'
                icon={<FilterOutlined />}
                iconPosition='start'
                color={assetTypeFilterActive ? 'blue' : 'default'}
                >
                Asset Type
              </Button>
            </Cascader>
          </div>
        ),
        extra: (
          <Tooltip title="Clear filter">
            <Button
              icon={<CloseOutlined />}
              disabled={props.disabled}
              onClick={(e) => {
                e.stopPropagation();
                onFilterClear("assetType")
              }}
            />
          </Tooltip>
        )
      }
    ]
  };


  const clearSelection = () => handleOnChange([]);

  return (
    <Flex justify='flex-start' align='center' gap={4}>
      <Select
        disabled={props.disabled}
        filterOption={false}
        loading={assetsFetching}
        mode={props.multiple ? "multiple" : undefined}
        showSearch
        onFocus={handleOnSelectFocus}
        onSearch={setSearchQuery}
        onChange={handleOnChange}
        value={props.value}
        labelInValue
        style={{
          flex: 1
        }}
        maxTagCount={'responsive'}
        options={
          (assetOptions ?? []).map((option) => ({
            ...option,
            label: (
              <Tooltip title={option.inUse ? "This asset is already on a work order" : option.details || option.label}>
               <Flex align="center" gap={4}>
                 {option.inUse && (
                  <WorkOrderAssetOpenWorkOrdersModal
                    assetId={option.value}
                    enableEdit={props.enableEdit}
                    triggerContent={<Button type="link" size="small">View Work Orders</Button>}
                  />
                )}
                {option.inUse && <WarningOutlined style={{ color: "orange" }} />}
                {option.label}
                
              </Flex>
            </Tooltip>
            ),
          }))
        }
        dropdownRender={(menu) => (
          <>
            {menu}
            {props.multiple && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Flex justify='space-between' align='center'>
                  <Space style={{ padding: '0 8px 4px' }} size='middle'>
                    <Button onClick={handleOnSelectAll}>Select All</Button>
                    {selectedCount}
                  </Space>
                  <Button onClick={clearSelection}>Clear</Button>
                </Flex>
              </>
            )}
          </>
        )}
      />

      <Dropdown
        disabled={props.disabled}
        menu={filterMenu}
        trigger={['click']}
        placement={props.menuPlacement}
      >
        <Button variant='outlined' color={filterActive ? 'blue' : 'default'} icon={<FilterOutlined />} />
      </Dropdown>
    </Flex>
  )
}
